import { CANONICAL_SEARCH_INDEX, getLocalizedItemName, type CanonicalSearchEntry } from "@/lib/data";
import { defaultLocale, type Locale } from "@/lib/i18n/config";

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

export interface LocalizedSearchResult extends CanonicalSearchEntry {
  displayName: string;
  isNameFallback: boolean;
  sourceLocale: Locale;
}

/**
 * Per-locale localized copy of CANONICAL_SEARCH_INDEX, built once and reused. Was previously
 * rebuilt inline on every searchItems() call, i.e. on every header-search keystroke: mapping all
 * ~745 entries through getLocalizedItemName() (a translations Map lookup + object spread per
 * entry) before any filtering happened. Header search is mounted in the app shell on every route
 * (see components/layout/Header.tsx), which is why Clarity measured the same ~440ms INP on
 * unrelated routes ("/", "/recipes", "/shops") — they all pay this one shared cost. Locale
 * changes are rare (a handful of times per session at most) while keystrokes are frequent, so
 * caching per locale turns an O(index size) cost-per-keystroke into a one-time cost per locale.
 */
const localizedIndexByLocale = new Map<Locale, LocalizedSearchResult[]>();

function getLocalizedIndex(locale: Locale): LocalizedSearchResult[] {
  const cached = localizedIndexByLocale.get(locale);
  if (cached) return cached;
  const built = CANONICAL_SEARCH_INDEX.map((entry) => {
    const localizedName = getLocalizedItemName(entry.id, locale);
    return {
      ...entry,
      displayName: localizedName.text,
      isNameFallback: localizedName.isFallback,
      sourceLocale: localizedName.sourceLocale,
    };
  });
  localizedIndexByLocale.set(locale, built);
  return built;
}

/**
 * Local, dependency-free search over the canonical search index. Matches a
 * normalized substring against name, id, kind, tags, and aliases — no
 * fuzzy/Fuse.js/MiniSearch yet, that's a possible future upgrade once this
 * proves too limited.
 */
export function searchItems(query: string, locale: Locale = defaultLocale, limit = 20): LocalizedSearchResult[] {
  const q = normalize(query);
  if (!q) return [];

  return getLocalizedIndex(locale)
    .filter((entry) => {
      if (normalize(entry.displayName).includes(q)) return true;
      if (normalize(entry.name).includes(q)) return true;
      if (normalize(entry.id).includes(q)) return true;
      if (normalize(entry.kind).includes(q)) return true;
      if (entry.tags.some((t) => normalize(t).includes(q))) return true;
      if (entry.aliases.some((a) => normalize(a).includes(q))) return true;
      return false;
    })
    .slice(0, limit);
}
